import {autoserializeAs, inheritSerialization} from "dcerialize";
import type {EnumTranslations} from "../../helpers/EnumHelpers";
import {Existing, NumberedIdentifiableModel} from "../base/IdentifiableModel";
import type {ModificationTrackedModel} from "../base/ModificationTrackedModel";
import type {SoftDeletableModel} from "../base/SoftDeletableModel";
import {ModificationDetails} from "../ModificationDetails";
import {IdOnlyInspectionType, SimpleInspectionType} from "../visits/inspections/InspectionType";
import {IdOnlyArticleGroup} from "./ArticleGroup";

export enum ArticleType {
  Article = "A",
  AddOn = "E",
  Cost = "C",
}

export const articleTypeTranslations: EnumTranslations<ArticleType> = {
  get [ArticleType.Article](): string {
    return $localize`:@@article-type.article:Article`;
  },
  get [ArticleType.AddOn](): string {
    return $localize`:@@article-type.add-on:Add-On`;
  },
  get [ArticleType.Cost](): string {
    return $localize`:@@article-type.cost:Cost`;
  },
};

export enum ArticleCostCategory {
  InspectionCosts = "I",
  TravelAndAdditionalCosts = "T",
}

export const articleCostCategoryTranslations: EnumTranslations<ArticleCostCategory> = {
  get [ArticleCostCategory.InspectionCosts](): string {
    return $localize`:@@article-cost-category.inspection-cost:Inspection Costs`;
  },
  get [ArticleCostCategory.TravelAndAdditionalCosts](): string {
    return $localize`:@@article-cost-category.travel-and-additional-cost:Travel and Additional Costs`;
  },
};

export type ArticleNumber = number;

// noinspection TypeScriptRedundantGenericType
export class IdOnlyArticle implements NumberedIdentifiableModel<ArticleNumber> {
  @autoserializeAs(() => Number)
  public number: ArticleNumber;

  public get id(): ArticleNumber {
    return this.number;
  }

  public set id(value: ArticleNumber) {
    this.number = value;
  }

  public static existing(number: ArticleNumber): Existing<IdOnlyArticle> {
    return new IdOnlyArticle(number);
  }

  public constructor(number: ArticleNumber) {
    this.number = number;
  }
}

@inheritSerialization(() => IdOnlyArticle)
export abstract class BaseNameOnlyArticle extends IdOnlyArticle {
  @autoserializeAs(() => IdOnlyArticleGroup)
  public articleGroup?: Existing<IdOnlyArticleGroup>;
  @autoserializeAs(() => String)
  public type: ArticleType;
  @autoserializeAs(() => Boolean)
  public isFavorite: boolean;
  @autoserializeAs(() => Boolean)
  public isHidden: boolean;
}

/**
 * @sealed
 */
@inheritSerialization(() => BaseNameOnlyArticle)
export class NameOnlyArticle extends BaseNameOnlyArticle implements SoftDeletableModel {
  @autoserializeAs(() => String)
  public readonly name: string;
  @autoserializeAs(() => Boolean)
  public readonly isDeleted: boolean;

  public get displayName(): string {
    return `${this.number} - ${this.name}`;
  }
}

@inheritSerialization(() => BaseNameOnlyArticle)
export class WritableArticle extends BaseNameOnlyArticle {
  @autoserializeAs(() => String)
  public costCategory?: ArticleCostCategory;
  @autoserializeAs(() => IdOnlyInspectionType)
  public inspectionType?: Existing<IdOnlyInspectionType>;
}

@inheritSerialization(() => WritableArticle)
export class SimpleArticle extends WritableArticle implements SoftDeletableModel {
  @autoserializeAs(() => String)
  public readonly name: string;
  @autoserializeAs(() => Boolean)
  public readonly isDeleted: boolean;
  @autoserializeAs(() => SimpleInspectionType)
  public override inspectionType: Readonly<Existing<SimpleInspectionType>>;

  public get displayName(): string {
    return `${this.number} - ${this.name}`;
  }
}

/**
 * @sealed
 */
@inheritSerialization(() => SimpleArticle)
export class Article extends SimpleArticle implements ModificationTrackedModel {
  @autoserializeAs(() => ModificationDetails)
  public readonly modificationDetails?: ModificationDetails;
}

/**
 * @sealed
 */
export class ArticleTranslation implements ModificationTrackedModel {
  @autoserializeAs(() => String)
  public name: string;
  @autoserializeAs(() => ModificationDetails)
  public readonly modificationDetails?: ModificationDetails;

  public static createEmpty(): ArticleTranslation {
    const translation = new ArticleTranslation();
    translation.name = "";
    return translation;
  }
}
